import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { learningAPI, analyticsAPI } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';

const Navbar = () => {
    const navigate = useNavigate();
    const { t, language, changeLanguage } = useLanguage();
    const [dropdown, setDropdown] = useState(null);
    const [user, setUser] = useState(null);
    const [stats, setStats] = useState({ zencoins: 0, xp: 0, streak: 0 });
    const [hoveredMenuItem, setHoveredMenuItem] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mobileSubmenu, setMobileSubmenu] = useState(null);
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 1024);
    const [unreadNotifications, setUnreadNotifications] = useState(0);
    const [notifications, setNotifications] = useState([]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const navTo = useCallback((path) => {
        setMobileMenuOpen(false);
        setMobileSubmenu(null);
        navigate(path);
    }, [navigate]);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [mobileMenuOpen]);

    React.useEffect(() => {
        const fetchUserData = async () => {
            const userData = JSON.parse(localStorage.getItem('user') || 'null');
            setUser(userData);
            
            // Fetch real stats from backend
            if (userData && userData.email) {
                try {
                    const res = await learningAPI.getProfile();
                    const profile = res.data.profile;
                    setStats({
                        zencoins: profile.zen_coins || 0,
                        xp: profile.total_xp || 0,
                        streak: profile.current_streak || 0
                    });
                } catch (e) {
                    console.error("Failed to load stats from backend", e);
                    const totalZenCoins = parseInt(localStorage.getItem('totalZenCoins')) || 0;
                    setStats(prev => ({
                        ...prev,
                        zencoins: totalZenCoins
                    }));
                }
            }
        };

        fetchUserData();

        const handleStorageChange = () => fetchUserData();
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('zencoins-updated', handleStorageChange);
        
        const pollInterval = setInterval(() => fetchUserData(), 5000);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('zencoins-updated', handleStorageChange);
            clearInterval(pollInterval);
        };
    }, []);

    // Fetch notifications from backend
    useEffect(() => {
        const fetchNotifications = async () => {
            const userData = JSON.parse(localStorage.getItem('user') || 'null');
            
            if (userData && userData.email) {
                try {
                    const countRes = await analyticsAPI.getUnreadCount();
                    setUnreadNotifications(countRes.data.unread_count || 0);
                    
                    const notifRes = await analyticsAPI.getNotifications(5);
                    const transformedNotifications = notifRes.data.map(notif => ({
                        id: notif.id,
                        message: notif.message,
                        time: notif.created_at ? new Date(notif.created_at).toLocaleDateString() : 'Just now',
                        unread: !notif.read
                    }));
                    setNotifications(transformedNotifications);
                } catch (e) {
                    console.error("Failed to fetch notifications from backend", e);
                }
            }
        };

        fetchNotifications();
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        navigate('/login');
    };

    const handleLanguageChange = (lang) => {
        changeLanguage(lang);
        setDropdown(null);
    };

    const handleNotificationClick = async (notificationId) => {
        try {
            await analyticsAPI.markNotificationRead(notificationId);
            setNotifications(prev => 
                prev.map(notif => 
                    notif.id === notificationId ? { ...notif, unread: false } : notif
                )
            );
            setUnreadNotifications(prev => Math.max(0, prev - 1));
            window.dispatchEvent(new Event('notification-updated'));
        } catch (e) {
            console.error("Failed to mark notification as read", e);
        }
    };

    const getLanguageLabel = () => {
        const labels = { 'en': 'English', 'es': 'Spanish', 'ta': 'Tamil' };
        return labels[language] || 'English';
    };

    const handleMouseEnter = (menu) => setDropdown(menu);
    const handleMouseLeave = () => setDropdown(null);
    
    const getMenuItemStyle = (itemKey, isSignOut = false) => ({
        ...styles.menuItem,
        ...(hoveredMenuItem === itemKey ? styles.menuItemHover : {}),
        ...(isSignOut ? { color: '#dc2626' } : {})
    });

    // Mobile Navigation Drawer
    const renderMobileMenu = () => (
        <>
            <div style={styles.mobileOverlay} onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }} />
            <div style={styles.mobilePanel}>
                <div style={styles.mobilePanelHeader}>
                    <div style={styles.logo} onClick={() => navTo('/')}>
                        <img src="/images/logos/arivu-nest-logo.png" alt="ARIVUNEST" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                            <span style={{ fontSize: '16px', fontWeight: '800' }}>
                                <span style={{ color: '#059669' }}>ARIVU</span>NEST
                            </span>
                            <span style={{ fontSize: '9px', fontWeight: '800', color: '#047857', letterSpacing: '0.5px' }}>
                                R&D &bull; MSME GOVT OF INDIA
                            </span>
                        </div>
                    </div>
                    <button 
                        onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }} 
                        style={styles.mobileCloseBtn}
                        aria-label="Close navigation menu"
                    >
                        <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                {user && (
                    <div style={styles.mobileUserSection}>
                        <div style={styles.mobileAvatar}>{user.name ? user.name[0].toUpperCase() : 'U'}</div>
                        <div>
                            <div style={{ fontWeight: '700', fontSize: '16px', color: '#1f2937' }}>{user.name}</div>
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>{user.email}</div>
                        </div>
                    </div>
                )}

                <div style={styles.mobileNavList}>
                    {/* Primary Highlight: Verify Certificate */}
                    <div 
                        style={{
                            ...styles.mobileNavItem,
                            background: '#ecfdf5',
                            color: '#065f46',
                            fontWeight: '700',
                            borderLeft: '4px solid #059669'
                        }} 
                        onClick={() => navTo('/verify')}
                    >
                        <span>🛡️ Verify Certificate / Ref ID</span>
                        <span style={{ fontSize: '11px', background: '#059669', color: '#fff', padding: '2px 8px', borderRadius: '4px' }}>LIVE</span>
                    </div>

                    {/* Faculty & Council Directory */}
                    <div style={styles.mobileNavItem} onClick={() => navTo('/faculty')}>
                        👥 Faculty &amp; Council Directory
                    </div>

                    {/* Research & Publications */}
                    <div style={styles.mobileNavItem} onClick={() => navTo('/research')}>
                        🔬 Research &amp; Publications
                    </div>

                    {/* Student Internships */}
                    <div style={styles.mobileNavItem} onClick={() => navTo('/internships')}>
                        💼 Student Internships
                    </div>

                    {/* Institution Submenu */}
                    <div style={styles.mobileNavItem} onClick={() => setMobileSubmenu(mobileSubmenu === 'institution' ? null : 'institution')}>
                        <span>🏫 Institution</span>
                        <span>{mobileSubmenu === 'institution' ? '▴' : '▾'}</span>
                    </div>
                    {mobileSubmenu === 'institution' && (
                        <div style={styles.mobileSubmenu}>
                            <div style={styles.mobileSubItem} onClick={() => navTo('/about')}>About Arivu Nest R&D</div>
                            <div style={styles.mobileSubItem} onClick={() => navTo('/about')}>Executive Directorate</div>
                            <div style={styles.mobileSubItem} onClick={() => navTo('/methodology')}>Methodology</div>
                            <div style={styles.mobileSubItem} onClick={() => navTo('/pricing')}>Memberships &amp; MoUs</div>
                            <div style={styles.mobileSubItem} onClick={() => navTo('/contact')}>Operating Centers & Contact</div>
                        </div>
                    )}

                    {/* Learning Labs Submenu */}
                    <div style={styles.mobileNavItem} onClick={() => setMobileSubmenu(mobileSubmenu === 'learning' ? null : 'learning')}>
                        <span>🗣️ Learning Labs</span>
                        <span>{mobileSubmenu === 'learning' ? '▴' : '▾'}</span>
                    </div>
                    {mobileSubmenu === 'learning' && (
                        <div style={styles.mobileSubmenu}>
                            <div style={styles.mobileSubItem} onClick={() => navTo('/courses')}>AI Communication Labs</div>
                            <div style={styles.mobileSubItem} onClick={() => navTo('/live-classes')}>Live Classes</div>
                            <div style={styles.mobileSubItem} onClick={() => navTo('/courses/free')}>Curriculum & Drills</div>
                            <div style={styles.mobileSubItem} onClick={() => navTo('/success-stories')}>Success Stories</div>
                        </div>
                    )}

                    {/* Resources Submenu */}
                    <div style={styles.mobileNavItem} onClick={() => setMobileSubmenu(mobileSubmenu === 'resources' ? null : 'resources')}>
                        <span>📖 Resources</span>
                        <span>{mobileSubmenu === 'resources' ? '▴' : '▾'}</span>
                    </div>
                    {mobileSubmenu === 'resources' && (
                        <div style={styles.mobileSubmenu}>
                            <div style={styles.mobileSubItem} onClick={() => navTo('/docs')}>Docs & Guidelines</div>
                            <div style={styles.mobileSubItem} onClick={() => navTo('/blog')}>Research Blog</div>
                            <div style={styles.mobileSubItem} onClick={() => navTo('/faq')}>FAQs</div>
                        </div>
                    )}

                    {/* Join Advisory Council Callout */}
                    <a
                        href="https://forms.gle/UfkUncKELYenUJE46"
                        target="_blank"
                        rel="noreferrer"
                        style={{
                            ...styles.mobileNavItem,
                            textDecoration: 'none',
                            background: '#fefce8',
                            color: '#854d0e',
                            fontWeight: '700',
                            borderLeft: '4px solid #eab308'
                        }}
                    >
                        <span>✍️ Join Council (₹100 Induction)</span>
                        <span>↗</span>
                    </a>

                    {/* User Authenticated Options */}
                    {user && (
                        <>
                            <div style={styles.mobileDivider} />
                            {user.role === 'instructor' ? (
                                <div style={styles.mobileNavItem} onClick={() => navTo('/instructor-dashboard')}>📊 {t('instructorDashboard')}</div>
                            ) : user.role === 'admin' ? (
                                <div style={styles.mobileNavItem} onClick={() => navTo('/admin-dashboard')}>⚙️ {t('adminPanel')}</div>
                            ) : (
                                <div style={styles.mobileNavItem} onClick={() => navTo('/student-dashboard')}>👤 {t('myProfile')}</div>
                            )}
                            <div style={styles.mobileNavItem} onClick={() => navTo('/practice')}>🎯 {t('practice')}</div>
                            {user.role === 'student' && (
                                <div style={styles.mobileNavItem} onClick={() => navTo('/rewards')}>🎁 {t('rewards')}</div>
                            )}
                            <div style={styles.mobileNavItem} onClick={() => navTo('/settings')}>⚙️ {t('settings')}</div>
                            <div style={styles.mobileDivider} />
                            <div style={styles.mobileStatsRow}>
                                <div style={styles.mobileStatPill}>💰 {stats.zencoins} Coins</div>
                                <div style={styles.mobileStatPill}>📚 {stats.xp} XP</div>
                            </div>
                            <div style={{ ...styles.mobileNavItem, color: '#dc2626' }} onClick={handleLogout}>🚪 {t('signOut')}</div>
                        </>
                    )}
                </div>

                {!user && (
                    <div style={styles.mobileAuthButtons}>
                        <button onClick={() => navTo('/login')} style={styles.mobileLoginBtn}>{t('login')}</button>
                        <button onClick={() => navTo('/signup')} style={styles.mobileSignupBtn}>{t('signUp')}</button>
                    </div>
                )}
            </div>
        </>
    );

    return (
        <nav style={styles.navbar}>
            <div style={styles.container}>

                {/* 1. Official Brand Logo with Subtitle */}
                <div style={styles.logo} onClick={() => navigate('/')}>
                    <img src="/images/logos/arivu-nest-logo.png" alt="ARIVUNEST" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: '18px', fontWeight: '900', letterSpacing: '-0.02em', color: '#0f172a' }}>
                            <span style={{ color: '#059669' }}>ARIVU</span>NEST
                        </span>
                        <span style={{ fontSize: '9.5px', fontWeight: '800', color: '#047857', letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                            R&amp;D &bull; MSME GOVT OF INDIA
                        </span>
                    </div>
                </div>

                {/* 2. Desktop Navigation Links (Edge-to-edge, No wrapping) */}
                {!isMobile && (
                    <div style={styles.navLinks}>

                        {/* Institution Dropdown */}
                        <div style={styles.navItem} onMouseEnter={() => handleMouseEnter('institution')} onMouseLeave={handleMouseLeave}>
                            <span style={styles.linkText}>{t('institution')} ▾</span>
                            {dropdown === 'institution' && (
                                <div style={styles.dropdownMenu}>
                                    <Link to="/about" style={styles.dropdownItem}>{t('aboutUs')}</Link>
                                    <Link to="/about" style={styles.dropdownItem}>Executive Directorate</Link>
                                    <Link to="/methodology" style={styles.dropdownItem}>{t('methodology')}</Link>
                                    <Link to="/pricing" style={styles.dropdownItem}>Memberships &amp; MoUs</Link>
                                    <Link to="/contact" style={styles.dropdownItem}>{t('contactUs')}</Link>
                                </div>
                            )}
                        </div>

                        {/* Direct: Faculty & Advisory Council */}
                        <Link to="/faculty" style={styles.navItemStatic}>Faculty &amp; Council</Link>

                        {/* Direct: Research & Publications */}
                        <Link to="/research" style={styles.navItemStatic}>Research &amp; Publications</Link>

                        {/* Direct: Student Internships */}
                        <Link to="/internships" style={styles.navItemStatic}>Internships</Link>

                        {/* Learning Labs Dropdown */}
                        <div style={styles.navItem} onMouseEnter={() => handleMouseEnter('learning')} onMouseLeave={handleMouseLeave}>
                            <span style={styles.linkText}>Learning Labs ▾</span>
                            {dropdown === 'learning' && (
                                <div style={styles.dropdownMenu}>
                                    <Link to="/courses" style={styles.dropdownItem}>AI Communication Labs</Link>
                                    <Link to="/live-classes" style={styles.dropdownItem}>Live Interactive Classes</Link>
                                    <Link to="/courses/free" style={styles.dropdownItem}>Curriculum &amp; Drills</Link>
                                    <Link to="/success-stories" style={styles.dropdownItem}>{t('successStories')}</Link>
                                </div>
                            )}
                        </div>

                        {/* Resources Dropdown */}
                        <div style={styles.navItem} onMouseEnter={() => handleMouseEnter('resources')} onMouseLeave={handleMouseLeave}>
                            <span style={styles.linkText}>{t('resources')} ▾</span>
                            {dropdown === 'resources' && (
                                <div style={styles.dropdownMenu}>
                                    <Link to="/docs" style={styles.dropdownItem}>Docs &amp; Guidelines</Link>
                                    <Link to="/blog" style={styles.dropdownItem}>{t('blog')}</Link>
                                    <Link to="/faq" style={styles.dropdownItem}>{t('faqs')}</Link>
                                </div>
                            )}
                        </div>

                    </div>
                )}

                {/* 3. Action Buttons & Profile Controls (Filling the right side nicely) */}
                {!isMobile && (
                    <div style={styles.actionsContainer}>

                        {/* Join Council (₹100) Button */}
                        <a
                            href="https://forms.gle/UfkUncKELYenUJE46"
                            target="_blank"
                            rel="noreferrer"
                            style={{
                                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                color: '#92400e',
                                border: '1.5px solid #fcd34d',
                                borderRadius: '8px',
                                padding: '8px 14px',
                                fontSize: '13px',
                                fontWeight: '800',
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                whiteSpace: 'nowrap',
                                transition: 'transform 0.15s, box-shadow 0.15s'
                            }}
                        >
                            <span>✍️</span>
                            <span>Join Council (₹100)</span>
                        </a>

                        {/* 🌟 Direct CTA: Verify Certificate */}
                        <button
                            onClick={() => navigate('/verify')}
                            style={{
                                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '8px 16px',
                                fontSize: '13px',
                                fontWeight: '800',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                boxShadow: '0 2px 8px rgba(5, 150, 105, 0.25)',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <span>🛡️</span>
                            <span>Verify Certificate</span>
                        </button>

                        {/* Language Selector Dropdown */}
                        <div 
                            style={{ position: 'relative' }}
                            onMouseEnter={() => handleMouseEnter('language')} 
                            onMouseLeave={handleMouseLeave}
                        >
                            <button 
                                style={styles.iconButton}
                                title={`Current: ${getLanguageLabel()}`}
                                aria-label="Change language"
                            >
                                🌐
                            </button>
                            {dropdown === 'language' && (
                                <div style={styles.smallDropdown}>
                                    <div 
                                        style={{ ...styles.dropdownItem, backgroundColor: language === 'en' ? '#f3f4f6' : 'transparent' }}
                                        onClick={() => handleLanguageChange('en')}
                                    >
                                        🇬🇧 English
                                    </div>
                                    <div 
                                        style={{ ...styles.dropdownItem, backgroundColor: language === 'es' ? '#f3f4f6' : 'transparent' }}
                                        onClick={() => handleLanguageChange('es')}
                                    >
                                        🇪🇸 Spanish
                                    </div>
                                    <div 
                                        style={{ ...styles.dropdownItem, backgroundColor: language === 'ta' ? '#f3f4f6' : 'transparent' }}
                                        onClick={() => handleLanguageChange('ta')}
                                    >
                                        🇮🇳 Tamil
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Notification Bell Dropdown */}
                        <div 
                            style={{ position: 'relative' }}
                            onMouseEnter={() => handleMouseEnter('notifications')} 
                            onMouseLeave={handleMouseLeave}
                        >
                            <button 
                                style={{
                                    ...styles.iconButton,
                                    position: 'relative'
                                }}
                                title="Notifications"
                                aria-label="Notifications"
                            >
                                🔔
                                {unreadNotifications > 0 && (
                                    <span style={styles.notificationBadge}>
                                        {unreadNotifications > 9 ? '9+' : unreadNotifications}
                                    </span>
                                )}
                            </button>
                            {dropdown === 'notifications' && (
                                <div style={styles.notificationDropdown}>
                                    <div style={styles.notificationHeader}>{t('notifications')}</div>
                                    <div style={styles.notificationList}>
                                        {notifications.length > 0 ? (
                                            notifications.map(notif => (
                                                <div 
                                                    key={notif.id} 
                                                    style={{
                                                        ...styles.notificationItem, 
                                                        backgroundColor: notif.unread ? '#f0f9ff' : 'transparent'
                                                    }}
                                                    onClick={() => handleNotificationClick(notif.id)}
                                                >
                                                    <div style={styles.notificationMessage}>{notif.message}</div>
                                                    <div style={styles.notificationTime}>{notif.time}</div>
                                                </div>
                                            ))
                                        ) : (
                                            <div style={{ padding: '16px', textAlign: 'center', color: '#999' }}>{t('noNotifications')}</div>
                                        )}
                                    </div>
                                    <div style={styles.notificationFooter} onClick={() => { navigate('/notifications'); setDropdown(null); }}>
                                        {t('viewAllNotifications')} →
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* User Profile or Login/Signup */}
                        {user ? (
                            <div style={{ position: 'relative' }} onMouseEnter={() => handleMouseEnter('profile')} onMouseLeave={handleMouseLeave}>
                                <div style={styles.userTrigger}>
                                    <div style={styles.navAvatar}>{user.name ? user.name[0].toUpperCase() : 'U'}</div>
                                    <span style={{ fontSize: '18px', color: '#64748b' }}>▾</span>
                                </div>
                                {dropdown === 'profile' && (
                                    <div style={styles.profileDropdown}>
                                        <div style={styles.profileHeader}>
                                            <div style={styles.largeAvatar}>
                                                {user.name ? user.name[0].toUpperCase() : 'U'}
                                                <div style={styles.onlineStatus}></div>
                                            </div>
                                            <div>
                                                <div style={styles.profileName}>{user.name}</div>
                                                <div style={styles.profileEmail}>{user.email}</div>
                                            </div>
                                        </div>
                                        <div style={styles.statsRow}>
                                            <div style={styles.statPill}><div style={styles.coinIcon}>💰</div><span>{stats.zencoins}</span></div>
                                            <div style={styles.statPill}><div style={styles.xpIcon}>📚</div><span>{stats.xp}</span></div>
                                        </div>
                                        <div style={styles.menuList}>
                                            {user.role === 'instructor' ? (
                                                <div style={getMenuItemStyle('dashboard')} onClick={() => navigate('/instructor-dashboard')} onMouseEnter={() => setHoveredMenuItem('dashboard')} onMouseLeave={() => setHoveredMenuItem(null)}>
                                                    <span style={styles.menuIcon}>📊</span> {t('instructorDashboard')}
                                                </div>
                                            ) : user.role === 'admin' ? (
                                                <div style={getMenuItemStyle('dashboard')} onClick={() => navigate('/admin-dashboard')} onMouseEnter={() => setHoveredMenuItem('dashboard')} onMouseLeave={() => setHoveredMenuItem(null)}>
                                                    <span style={styles.menuIcon}>⚙️</span> {t('adminPanel')}
                                                </div>
                                            ) : (
                                                <div style={getMenuItemStyle('dashboard')} onClick={() => navigate('/student-dashboard')} onMouseEnter={() => setHoveredMenuItem('dashboard')} onMouseLeave={() => setHoveredMenuItem(null)}>
                                                    <span style={styles.menuIcon}>👤</span> {t('myProfile')}
                                                </div>
                                            )}
                                            {user.role === 'instructor' && (
                                                <div style={getMenuItemStyle('liveclasses')} onClick={() => navigate('/instructor/live-classes')} onMouseEnter={() => setHoveredMenuItem('liveclasses')} onMouseLeave={() => setHoveredMenuItem(null)}>
                                                    <span style={styles.menuIcon}>🎥</span> Manage Live Classes
                                                </div>
                                            )}
                                            <div style={getMenuItemStyle('courses')} onClick={() => navigate('/courses')} onMouseEnter={() => setHoveredMenuItem('courses')} onMouseLeave={() => setHoveredMenuItem(null)}>
                                                <span style={styles.menuIcon}>📚</span> {user.role === 'instructor' ? t('myCourses') : t('browseCourses')}
                                            </div>
                                            {(user.role === 'student' || user.role === 'instructor') && (
                                                <div style={getMenuItemStyle('practice')} onClick={() => navigate('/practice')} onMouseEnter={() => setHoveredMenuItem('practice')} onMouseLeave={() => setHoveredMenuItem(null)}>
                                                    <span style={styles.menuIcon}>🎯</span> {t('practice')}
                                                </div>
                                            )}
                                            {user.role === 'student' && (
                                                <div style={getMenuItemStyle('rewards')} onClick={() => navigate('/rewards')} onMouseEnter={() => setHoveredMenuItem('rewards')} onMouseLeave={() => setHoveredMenuItem(null)}>
                                                    <span style={styles.menuIcon}>🎁</span> {t('rewards')}
                                                </div>
                                            )}
                                            <div style={getMenuItemStyle('settings')} onClick={() => navigate('/settings')} onMouseEnter={() => setHoveredMenuItem('settings')} onMouseLeave={() => setHoveredMenuItem(null)}>
                                                <span style={styles.menuIcon}>⚙️</span> {t('settings')}
                                            </div>
                                            <div style={getMenuItemStyle('help')} onClick={() => navigate('/faq')} onMouseEnter={() => setHoveredMenuItem('help')} onMouseLeave={() => setHoveredMenuItem(null)}>
                                                <span style={styles.menuIcon}>❓</span> {t('help')}
                                            </div>
                                            <div style={styles.divider}></div>
                                            <div style={getMenuItemStyle('signout', true)} onClick={handleLogout} onMouseEnter={() => setHoveredMenuItem('signout')} onMouseLeave={() => setHoveredMenuItem(null)}>
                                                <span style={styles.menuIcon}>🚪</span> {t('signOut')}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={styles.authButtons}>
                                <button onClick={() => navigate('/login')} style={styles.loginBtn}>{t('login')}</button>
                                <button onClick={() => navigate('/signup')} style={styles.signupBtn}>{t('signUp')}</button>
                            </div>
                        )}
                    </div>
                )}

                {/* Mobile Hamburger Toggle */}
                {isMobile && (
                    <button 
                        onClick={() => setMobileMenuOpen(true)} 
                        style={{
                            ...styles.hamburger,
                            visibility: mobileMenuOpen ? 'hidden' : 'visible',
                            pointerEvents: mobileMenuOpen ? 'none' : 'auto',
                        }} 
                        aria-label="Open navigation menu"
                    >
                        <span style={styles.hamburgerLine} />
                        <span style={styles.hamburgerLine} />
                        <span style={styles.hamburgerLine} />
                    </button>
                )}
            </div>

            {isMobile && mobileMenuOpen && renderMobileMenu()}
        </nav>
    );
};

const styles = {
    navbar: {
        backgroundColor: '#ffffff',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        height: '72px',
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid #e2e8f0',
        width: '100%',
    },
    container: {
        width: '100%',
        maxWidth: '100%',
        margin: '0',
        padding: '0 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '16px',
    },
    logo: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#333',
        cursor: 'pointer',
        fontFamily: "'Inter', sans-serif",
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexShrink: 0,
        whiteSpace: 'nowrap',
    },
    navLinks: {
        display: 'flex',
        gap: '24px',
        alignItems: 'center',
        height: '72px',
        flexWrap: 'nowrap',
        flexShrink: 0,
    },
    navItem: {
        position: 'relative',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    navItemStatic: {
        textDecoration: 'none',
        color: '#1e293b',
        fontWeight: '600',
        fontSize: '14.5px',
        cursor: 'pointer',
        transition: 'color 0.2s',
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
    },
    linkText: {
        color: '#1e293b',
        fontWeight: '600',
        fontSize: '14.5px',
        whiteSpace: 'nowrap',
    },
    userTrigger: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        cursor: 'pointer',
        padding: '4px',
    },
    navAvatar: {
        width: '38px',
        height: '38px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
        fontSize: '16px',
    },
    actionsContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexShrink: 0,
        whiteSpace: 'nowrap',
    },
    iconButton: {
        background: 'none',
        border: 'none',
        fontSize: '20px',
        cursor: 'pointer',
        padding: '6px 8px',
        minHeight: '40px',
        minWidth: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8px',
        transition: 'all 0.2s ease',
        color: '#333',
    },
    notificationBadge: {
        position: 'absolute',
        top: '-4px',
        right: '-4px',
        backgroundColor: '#ef4444',
        color: 'white',
        borderRadius: '50%',
        width: '20px',
        height: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '11px',
        fontWeight: 'bold',
        border: '2px solid white',
    },
    smallDropdown: {
        position: 'absolute',
        top: '50px',
        right: '0',
        backgroundColor: '#ffffff',
        boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
        borderRadius: '12px',
        width: '170px',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #eee',
        zIndex: 1100,
        overflow: 'hidden',
    },
    notificationDropdown: {
        position: 'absolute',
        top: '50px',
        right: '0',
        backgroundColor: '#ffffff',
        boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
        borderRadius: '12px',
        width: '340px',
        maxHeight: '400px',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #eee',
        zIndex: 1100,
    },
    notificationHeader: {
        padding: '12px 16px',
        fontWeight: '700',
        fontSize: '14.5px',
        borderBottom: '1px solid #eee',
        color: '#1f2937',
    },
    notificationList: {
        flex: 1,
        overflowY: 'auto',
        maxHeight: '280px',
    },
    notificationItem: {
        padding: '12px 16px',
        borderBottom: '1px solid #f0f0f0',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    notificationMessage: {
        fontSize: '13.5px',
        fontWeight: '500',
        color: '#1f2937',
        marginBottom: '4px',
    },
    notificationTime: {
        fontSize: '11.5px',
        color: '#999',
    },
    notificationFooter: {
        padding: '10px 16px',
        textAlign: 'center',
        fontSize: '12.5px',
        fontWeight: '600',
        color: '#059669',
        cursor: 'pointer',
        borderTop: '1px solid #eee',
        transition: 'all 0.2s',
    },
    profileDropdown: {
        position: 'absolute',
        top: '60px',
        right: '0',
        backgroundColor: '#ffffff',
        boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
        borderRadius: '16px',
        width: '300px',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #eee',
        padding: '18px',
        zIndex: 1100,
    },
    profileHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '16px',
    },
    largeAvatar: {
        width: '52px',
        height: '52px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
        fontSize: '22px',
        position: 'relative',
    },
    onlineStatus: {
        position: 'absolute',
        bottom: '2px',
        right: '2px',
        width: '11px',
        height: '11px',
        background: '#22c55e',
        borderRadius: '50%',
        border: '2px solid white',
    },
    profileName: { fontWeight: '700', fontSize: '16px', color: '#1f2937' },
    profileEmail: { fontSize: '12.5px', color: '#6b7280' },
    statsRow: { display: 'flex', gap: '8px', marginBottom: '16px' },
    statPill: {
        flex: 1,
        background: '#f3f4f6',
        padding: '8px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        fontWeight: '700',
        fontSize: '13px',
        color: '#374151',
    },
    coinIcon: { fontSize: '18px' },
    xpIcon: { fontSize: '16px' },
    menuList: { display: 'flex', flexDirection: 'column', gap: '4px' },
    menuItem: {
        padding: '10px 12px',
        borderRadius: '8px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        color: '#4b5563',
        fontWeight: '500',
        transition: 'all 0.2s',
        fontSize: '14px',
    },
    menuItemHover: { backgroundColor: '#f3f4f6', transform: 'translateX(2px)' },
    menuIcon: { fontSize: '16px', width: '20px', textAlign: 'center' },
    dropdownMenu: {
        position: 'absolute',
        top: '70px',
        left: '-10px',
        backgroundColor: '#ffffff',
        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
        borderRadius: '12px',
        padding: '10px 0',
        minWidth: '220px',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #e2e8f0',
        zIndex: 1100
    },
    dropdownItem: {
        padding: '9px 18px',
        textDecoration: 'none',
        color: '#334155',
        fontSize: '13.5px',
        fontWeight: '600',
        transition: 'all 0.2s',
        display: 'block',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    divider: { height: '1px', backgroundColor: '#eee', margin: '4px 0' },
    authButtons: { display: 'flex', gap: '8px', alignItems: 'center', whiteSpace: 'nowrap' },
    loginBtn: {
        backgroundColor: 'transparent',
        color: '#1e293b',
        padding: '8px 16px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        border: 'none',
        whiteSpace: 'nowrap',
    },
    signupBtn: {
        backgroundColor: '#059669',
        color: '#ffffff',
        padding: '8px 18px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '700',
        cursor: 'pointer',
        border: 'none',
        boxShadow: '0 2px 8px rgba(5, 150, 105, 0.25)',
        whiteSpace: 'nowrap',
    },
    // Mobile styles
    hamburger: {
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '8px',
        zIndex: 10,
    },
    hamburgerLine: {
        width: '24px',
        height: '2.5px',
        backgroundColor: '#333',
        borderRadius: '2px',
        transition: 'all 0.3s ease',
    },
    mobileOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1050,
    },
    mobilePanel: {
        position: 'fixed',
        top: 0,
        right: 0,
        width: '85%',
        maxWidth: '360px',
        height: '100vh',
        backgroundColor: '#fff',
        zIndex: 1100,
        overflowY: 'auto',
        boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
        display: 'flex',
        flexDirection: 'column',
    },
    mobilePanelHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 20px',
        borderBottom: '1px solid #eee',
    },
    mobileCloseBtn: {
        background: '#f1f5f9',
        border: 'none',
        borderRadius: '50%',
        width: '36px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: '#334155',
        padding: 0,
        transition: 'all 0.2s ease',
        flexShrink: 0,
    },
    mobileUserSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '16px 20px',
        borderBottom: '1px solid #f3f4f6',
        backgroundColor: '#f9fafb',
    },
    mobileAvatar: {
        width: '44px',
        height: '44px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
        fontSize: '18px',
        flexShrink: 0,
    },
    mobileNavList: {
        display: 'flex',
        flexDirection: 'column',
        padding: '8px 0',
        flex: 1,
    },
    mobileNavItem: {
        padding: '13px 20px',
        fontSize: '14.5px',
        fontWeight: '600',
        color: '#334155',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #f1f5f9',
    },
    mobileSubmenu: {
        backgroundColor: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
    },
    mobileSubItem: {
        padding: '11px 20px 11px 40px',
        fontSize: '13.5px',
        color: '#64748b',
        fontWeight: '500',
        cursor: 'pointer',
        borderBottom: '1px solid #f1f5f9',
    },
    mobileDivider: {
        height: '1px',
        backgroundColor: '#e5e7eb',
        margin: '4px 0',
    },
    mobileStatsRow: {
        display: 'flex',
        gap: '10px',
        padding: '12px 20px',
    },
    mobileStatPill: {
        flex: 1,
        background: '#f3f4f6',
        padding: '10px',
        borderRadius: '8px',
        textAlign: 'center',
        fontWeight: '600',
        fontSize: '13px',
        color: '#374151',
    },
    mobileAuthButtons: {
        padding: '16px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        borderTop: '1px solid #eee',
        marginTop: 'auto',
    },
    mobileLoginBtn: {
        width: '100%',
        padding: '12px',
        borderRadius: '8px',
        border: '2px solid #059669',
        backgroundColor: 'transparent',
        color: '#059669',
        fontWeight: '700',
        fontSize: '14.5px',
        cursor: 'pointer',
    },
    mobileSignupBtn: {
        width: '100%',
        padding: '12px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: '#059669',
        color: '#fff',
        fontWeight: '700',
        fontSize: '14.5px',
        cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)',
    },
};

export default Navbar;
